// /src/engine/connectorSnap.ts

import * as THREE from 'three';
import type { SnapSettings } from '@/store/modelStore';
import {
  getConnectorWorldDir,
  getConnectorWorldPos,
  getObjectConnectors,
  connectorsCompatible,
  type Connector,
} from './connectors';
import { snapValue } from './snap';

const _aPos = new THREE.Vector3();
const _bPos = new THREE.Vector3();
const _aDir = new THREE.Vector3();
const _bDir = new THREE.Vector3();
const _delta = new THREE.Vector3();
const _worldPos = new THREE.Vector3();
const _qAlign = new THREE.Quaternion();
const _qWorld = new THREE.Quaternion();
const _qParent = new THREE.Quaternion();
const _qParentInv = new THREE.Quaternion();

export type ConnectorMatch = {
  other: THREE.Object3D;
  a: Connector;
  b: Connector;
  aPosWorld: THREE.Vector3;
  bPosWorld: THREE.Vector3;
  score: number;
};

export type ConnectorPairFilter = (
  a: Connector,
  b: Connector,
  aDirWorld: THREE.Vector3,
  bDirWorld: THREE.Vector3,
  deltaWorld: THREE.Vector3
) => boolean;

export type FindConnectorMatchOptions = {
  /**
   * Optional filter to include/exclude connector pairs before scoring.
   * Return true to allow the pair, false to skip it.
   */
  filter?: ConnectorPairFilter;
};

function applyWorldQuaternionDelta(obj: THREE.Object3D, dqWorld: THREE.Quaternion) {
  obj.getWorldQuaternion(_qWorld);
  _qWorld.premultiply(dqWorld);

  if (obj.parent) {
    obj.parent.getWorldQuaternion(_qParent);
    _qParentInv.copy(_qParent).invert();
    obj.quaternion.copy(_qParentInv).multiply(_qWorld);
  } else {
    obj.quaternion.copy(_qWorld);
  }
}

function setWorldPosition(obj: THREE.Object3D, worldPos: THREE.Vector3) {
  _worldPos.copy(worldPos);
  if (obj.parent) obj.parent.worldToLocal(_worldPos);
  obj.position.copy(_worldPos);
}

export function findBestConnectorMatch(
  selected: THREE.Object3D,
  others: THREE.Object3D[],
  settings: SnapSettings,
  opts?: FindConnectorMatchOptions
): ConnectorMatch | null {
  if (!settings.enabled || !settings.connectorSnap) return null;

  const aCons = getObjectConnectors(selected);
  if (aCons.length === 0) return null;

  selected.updateMatrix();
  selected.updateMatrixWorld(true);

  const threshold = Math.max(1, settings.connectorThreshold ?? 12);
  const thr2 = threshold * threshold;

  // Studio-style stacking behavior:
  // For vertical "stack" connectors (stud/tube/etc) we want snapping to engage even when the
  // part is one (or a few) brick-heights above/below the target.
  //
  // We therefore use an anisotropic acceptance/scoring metric:
  //   - Horizontal (XZ) must be within connectorThreshold
  //   - Vertical (Y) can be larger (up to a derived tolerance),
  //     but is penalized so nearer layers win.
  const heightStep =
    settings.heightSnap && Number.isFinite(settings.height) && settings.height > 0 ? settings.height : 8;
  const verticalTol = Math.max(24, heightStep * 12, threshold * 6); // ~ up to ~4 bricks by default
  const verticalWeight = 0.1;

  // Candidate pool for multi-connector agreement.
  const multi = !!(settings as any).multiConnector;
  const maxCandidates = multi ? 10 : 1;
  const candidates: ConnectorMatch[] = [];

  const pushCandidate = (m: ConnectorMatch) => {
    if (candidates.length < maxCandidates) {
      candidates.push(m);
      candidates.sort((x, y) => x.score - y.score);
      return;
    }
    const worst = candidates[candidates.length - 1];
    if (m.score >= worst.score) return;
    candidates[candidates.length - 1] = m;
    candidates.sort((x, y) => x.score - y.score);
  };

  for (const other of others) {
    if (other === selected) continue;
    if (!other.visible) continue;

    const bCons = getObjectConnectors(other);
    if (bCons.length === 0) continue;

    other.updateMatrix();
    other.updateMatrixWorld(true);

    for (const a of aCons) {
      getConnectorWorldPos(selected, a, _aPos);
      getConnectorWorldDir(selected, a, _aDir);

      for (const b of bCons) {
        if (!connectorsCompatible(a, b)) continue;

        getConnectorWorldPos(other, b, _bPos);

        _delta.subVectors(_bPos, _aPos);
        const dx = _delta.x;
        const dy = _delta.y;
        const dz = _delta.z;
        const planarD2 = dx * dx + dz * dz;

        // If we're not close in XZ, bail early.
        // (Also implies full 3D distance can't be within threshold.)
        if (planarD2 > thr2) continue;

        // Direction alignment heuristic:
        // - For many connectors we want opposite (dot ~ -1), but hinges can be aligned (dot ~ +1).
        getConnectorWorldDir(other, b, _bDir);

        // Optional caller filter (e.g. exclude vertical "lift" pairs during translate).
        if (opts?.filter && !opts.filter(a, b, _aDir, _bDir, _delta)) continue;

        const dot = _aDir.dot(_bDir);
        const align = Math.min(Math.abs(dot + 1), Math.abs(dot - 1));
        const dirPenalty = align * 1000;

        // Determine whether this is a vertical "stack" style connector match.
        // We treat "near vertical" axes as stackable and allow larger Y deltas.
        const aVert = Math.abs(_aDir.y) > 0.85;
        const bVert = Math.abs(_bDir.y) > 0.85;
        const isVertical = aVert && bVert;

        // Distance score:
        // - Vertical connectors: allow larger Y delta, but penalize it in scoring.
        // - Non-vertical connectors: strict 3D threshold (original behavior).
        let distScore = 0;
        if (isVertical) {
          if (Math.abs(dy) > verticalTol) continue;
          distScore = planarD2 + dy * dy * verticalWeight;
        } else {
          const d2 = planarD2 + dy * dy; // full 3D
          if (d2 > thr2) continue;
          distScore = d2;
        }

        const score = distScore + dirPenalty;
        pushCandidate({
          other,
          a,
          b,
          aPosWorld: _aPos.clone(),
          bPosWorld: _bPos.clone(),
          score,
        });
      }
    }
  }
  
  if (candidates.length === 0) return null;
  if (!multi || candidates.length === 1) return candidates[0];

  // Multi-connector agreement scoring: pick the delta that also aligns other connectors.
  const agreeThr = Math.max(1, Math.min(4, threshold * 0.35));
  const agreeThr2 = agreeThr * agreeThr;

  let best = candidates[0];
  let bestAgree = -1;

  for (const cand of candidates) {
    const bCons = getObjectConnectors(cand.other);
    if (!bCons.length) continue;
    cand.other.updateMatrix();
    cand.other.updateMatrixWorld(true);

    const deltaW = _delta.copy(cand.bPosWorld).sub(cand.aPosWorld);

    // Precompute world positions for candidate other connectors.
    const bPosList: Array<{ c: Connector; p: THREE.Vector3 }> = [];
    for (const b of bCons) {
      if (!connectorsCompatible(cand.a, b) && !connectorsCompatible(b, cand.a)) {
        // still allow other compatible types below; just keep list full
      }
      bPosList.push({ c: b, p: getConnectorWorldPos(cand.other, b, new THREE.Vector3()).clone() });
    }

    let agree = 0;
    for (const a of aCons) {
      // Only count connectors compatible with the other side.
      getConnectorWorldPos(selected, a, _aPos);
      _aPos.add(deltaW);

      let ok = false;
      for (const bb of bPosList) {
        if (!connectorsCompatible(a, bb.c)) continue;
        if (_aPos.distanceToSquared(bb.p) <= agreeThr2) {
          ok = true;
          break;
        }
      }
      if (ok) agree++;
    }

    if (agree > bestAgree || (agree === bestAgree && cand.score < best.score)) {
      bestAgree = agree;
      best = cand;
    }
  }

  return best;
}

/**
 * Specialized helper for LEGO-style "stack on studs" while dragging:
 * - Only consider vertical stud/tube-like pairs
 * - Require selected connector to be the underside (female) and the other to be the stud (male)
 */
export function findBestStackMatch(
  selected: THREE.Object3D,
  others: THREE.Object3D[],
  settings: SnapSettings
): ConnectorMatch | null {
  if (!settings.enabled || !settings.connectorSnap) return null;

  return findBestConnectorMatch(selected, others, settings, {
    filter: (a, b, aDirW, bDirW) => {
      const aVert = Math.abs(aDirW.y) > 0.85;
      const bVert = Math.abs(bDirW.y) > 0.85;
      if (!aVert || !bVert) return false;

      // Facing each other (selected down, other up)
      if (aDirW.y > -0.5) return false;
      if (bDirW.y < 0.5) return false;

      // Prefer explicit gender pairing when available (LDCad)
      if (a.gender && b.gender) {
        if (a.gender !== 'F' || b.gender !== 'M') return false;
      } else {
        // Procedural fallback
        if (!(a.kind === 'tube' && b.kind === 'stud')) return false;
      }

      return true;
    },
  });
}

export function applyConnectorSnapFromMatch(
  selected: THREE.Object3D,
  match: ConnectorMatch,
  settings: SnapSettings
): boolean {
  if (!settings.enabled || !settings.connectorSnap) return false;
  if (!match) return false;

  // Optional rotate-to-align (world-space)
  if ((settings as any).alignOnConnect) {
    getConnectorWorldDir(selected, match.a, _aDir);
    getConnectorWorldDir(match.other, match.b, _bDir);
    const target = _bDir.clone().multiplyScalar(-1); // face-to-face by default
    _qAlign.setFromUnitVectors(_aDir.normalize(), target.normalize());
    applyWorldQuaternionDelta(selected, _qAlign);
    selected.updateMatrix();
    selected.updateMatrixWorld(true);
  }

  // Recompute positions after any rotation and translate in WORLD space.
  getConnectorWorldPos(selected, match.a, _aPos);
  getConnectorWorldPos(match.other, match.b, _bPos);
  const bestDeltaW = _delta.copy(_bPos).sub(_aPos);

  selected.getWorldPosition(_worldPos);
  _worldPos.add(bestDeltaW);

  // Keep aligned to editor grid after connector snap (world-space).
  if (settings.gridSnap) {
    _worldPos.x = snapValue(_worldPos.x, settings.grid);
    _worldPos.z = snapValue(_worldPos.z, settings.grid);
  }
  if (settings.heightSnap) {
    _worldPos.y = snapValue(_worldPos.y, settings.height);
  }

  setWorldPosition(selected, _worldPos);

  selected.updateMatrix();
  return true;
}

export function applyConnectorSnap(
  selected: THREE.Object3D,
  others: THREE.Object3D[],
  settings: SnapSettings,
  opts?: FindConnectorMatchOptions
): boolean {
  if (!settings.enabled || !settings.connectorSnap) return false;

  const match = findBestConnectorMatch(selected, others, settings, opts);
  if (!match) return false;

  return applyConnectorSnapFromMatch(selected, match, settings);
}

export function applyLockedConnectorConstraint(
  selected: THREE.Object3D,
  other: THREE.Object3D,
  aId: string,
  bId: string,
  settings: SnapSettings
): boolean {
  if (!settings.enabled || !settings.connectorSnap) return false;
  if (!aId || !bId) return false;

  const aCons = getObjectConnectors(selected);
  const bCons = getObjectConnectors(other);
  if (!aCons.length || !bCons.length) return false;

  const a = aCons.find((c) => c.id === aId);
  const b = bCons.find((c) => c.id === bId);
  if (!a || !b) return false;
  if (!connectorsCompatible(a, b)) return false;

  selected.updateMatrixWorld(true);
  other.updateMatrixWorld(true);

  getConnectorWorldPos(selected, a, _aPos);
  getConnectorWorldPos(other, b, _bPos);

  _delta.subVectors(_bPos, _aPos); // world delta
  selected.getWorldPosition(_worldPos);
  _worldPos.add(_delta);
  setWorldPosition(selected, _worldPos);

  // In hinge mode we want the connector to stay EXACTLY locked, so we intentionally
  // do NOT grid/height snap here (grid snapping would break the hinge pivot).

  selected.updateMatrix();
  return true;
}

const _axis = new THREE.Vector3();
const _q0inv = new THREE.Quaternion();
const _dq = new THREE.Quaternion();
const _twist = new THREE.Quaternion();
const _vPart = new THREE.Vector3();

function signedAngleFromTwist(q: THREE.Quaternion, axisUnit: THREE.Vector3): number {
  // For a pure twist around axis: q.xyz = axis * sin(angle/2), w = cos(angle/2)
  const s = q.x * axisUnit.x + q.y * axisUnit.y + q.z * axisUnit.z; // sin(angle/2) (signed)
  return 2 * Math.atan2(s, q.w);
}

function makeTwist(axisUnit: THREE.Vector3, angleRad: number, out = new THREE.Quaternion()): THREE.Quaternion {
  return out.setFromAxisAngle(axisUnit, angleRad);
}

function projectToTwist(dq: THREE.Quaternion, axisUnit: THREE.Vector3, out = new THREE.Quaternion()): THREE.Quaternion {
  // Swing-twist decomposition (world-space axis):
  // twist = normalize( [axis * dot(v,axis), w] )
  _vPart.set(dq.x, dq.y, dq.z);
  const d = _vPart.dot(axisUnit);
  out.set(axisUnit.x * d, axisUnit.y * d, axisUnit.z * d, dq.w);
  return out.normalize();
}

export function applyHingeAxisConstraint(
  selected: THREE.Object3D,
  other: THREE.Object3D,
  aId: string,
  bId: string,
  startQuatWorld: THREE.Quaternion,
  settings: SnapSettings
): boolean {
  if (!settings.enabled || !settings.connectorSnap) return false;
  if (!aId || !bId) return false;

  const aCons = getObjectConnectors(selected);
  const bCons = getObjectConnectors(other);
  if (!aCons.length || !bCons.length) return false;

  const a = aCons.find((c) => c.id === aId);
  const b = bCons.find((c) => c.id === bId);
  if (!a || !b) return false;
  if (!connectorsCompatible(a, b)) return false;

  selected.updateMatrixWorld(true);
  other.updateMatrixWorld(true);

  // Axis and pivot are defined by the "other" connector.
  getConnectorWorldDir(other, b, _axis);
  if (_axis.lengthSq() < 1e-10) _axis.set(0, 1, 0);
  _axis.normalize();

  // Project current rotation delta onto the hinge axis (twist-only).
  // dq(world) = qCurrent * inv(qStart)
  const qCurWorld = selected.getWorldQuaternion(_qWorld);
  _q0inv.copy(startQuatWorld).invert();
  _dq.copy(qCurWorld).multiply(_q0inv);
  projectToTwist(_dq, _axis, _twist);

  // Optional snap: quantize hinge angle using rotDeg
  let ang = signedAngleFromTwist(_twist, _axis);
  if (settings.rotSnap && Number.isFinite(settings.rotDeg) && settings.rotDeg > 0) {
    const step = THREE.MathUtils.degToRad(settings.rotDeg);
    ang = Math.round(ang / step) * step;
  }

  makeTwist(_axis, ang, _twist);

  // Apply limited rotation in WORLD: qNewWorld = twist * qStartWorld
  const qNewWorld = new THREE.Quaternion().copy(_twist).multiply(startQuatWorld);
  if (selected.parent) {
    selected.parent.getWorldQuaternion(_qParent);
    _qParentInv.copy(_qParent).invert();
    selected.quaternion.copy(_qParentInv).multiply(qNewWorld);
  } else {
    selected.quaternion.copy(qNewWorld);
  }
  selected.updateMatrix();
  selected.updateMatrixWorld(true);

  // Keep pivot locked by translating the selected connector onto the other connector.
  getConnectorWorldPos(other, b, _bPos);
  getConnectorWorldPos(selected, a, _aPos);
  _delta.subVectors(_bPos, _aPos);

  selected.getWorldPosition(_worldPos);
  _worldPos.add(_delta);
  setWorldPosition(selected, _worldPos);

  selected.updateMatrix();
  return true;
}
